import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useApi } from '~/contexts';
import { colors, weights } from '~/styles';
import { EmptyState, Mono, Table, Td, Th } from './shared';
import MemberDisplay from './MemberDisplay';
import WorkspaceDisplay from './WorkspaceDisplay';
import TraceTimeline from './TraceTimeline';
import { assembleTrace } from './traceUtils';

const HeaderTable = styled(Table)`
  margin-bottom: 1.5rem;
`;

const Preview = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 30rem;
  font-weight: ${weights.medium};
`;

const PreviewText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeletedBadge = styled.span`
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: ${weights.bold};
  color: ${colors.danger};
  background: ${colors.danger}15;
  padding: 0.125rem 0.375rem;
  border-radius: 0.125rem;
`;

export default function ConversationDetail() {
  const { conversationId } = useParams();
  const api = useApi();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchTrace = async () => {
      setIsLoading(true);
      setData(null);
      try {
        const { data: result } = await api.admin.aiReplay.getTrace(conversationId);
        if (!cancelled) setData(result);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchTrace();
    return () => {
      cancelled = true;
    };
  }, [api, conversationId]);

  const turns = useMemo(() => {
    if (!data?.events) return [];
    return assembleTrace(data.events);
  }, [data]);

  const userTurns = turns.filter((t) => t.userMessage);
  const userTurnCount = userTurns.length;
  const lastMessageEvent = [...(data?.events || [])]
    .reverse()
    .find((e) => e.eventType === 'message' && e.role === 'user');

  return (
    <>
      {isLoading && <EmptyState>Loading conversation trace...</EmptyState>}

      {!isLoading && !data && <EmptyState>Conversation not found.</EmptyState>}

      {!isLoading && data && (
        <>
          <HeaderTable>
            <thead>
              <tr>
                <Th>Conversation</Th>
                <Th>Workspace</Th>
                <Th>Member</Th>
                <Th>Turns</Th>
                <Th>Last Activity</Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Td>
                  <Preview>
                    <PreviewText>{data.title || '-'}</PreviewText>
                    {data.isDeleted && <DeletedBadge>Deleted</DeletedBadge>}
                  </Preview>
                  <Mono>{conversationId}</Mono>
                </Td>
                <Td>
                  <WorkspaceDisplay workspace={data.workspace} />
                </Td>
                <Td>
                  <MemberDisplay member={data.member} />
                </Td>
                <Td>{userTurnCount}</Td>
                <Td title={lastMessageEvent?.timestamp}>
                  {lastMessageEvent?.timestamp ? moment(lastMessageEvent.timestamp).fromNow() : '-'}
                </Td>
              </tr>
            </tbody>
          </HeaderTable>

          <TraceTimeline turns={turns} />
        </>
      )}
    </>
  );
}
